import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { Menu, Button, Dropdown, Space } from 'antd';
import { Search } from 'ui';
import { PlusOutlined, DownOutlined } from '@ant-design/icons';
import { InstructionButton } from 'components/VideoInstructions';
import FlexRow from "components/FlexRow";
import apiClient from "utils/apiClient";
import ImportModal from "../../ImportModal";
import UsersExportModal from "../UsersExportModal";
import { UsersListToolbarProps } from '../types';
import { FilterModal } from "components/Modals";
import { connect } from 'react-redux';
import generalHelpers from "utils/generalHelpers";
import {useT} from "../../../hooks/Locale/useT";

const mapStateToProps = (state: any) => {
    return {
        session: state.session
    }
};

const UsersListToolbar: React.FC<UsersListToolbarProps> = ({
    session,
    setSearch,
    search,
    setFilter,
    filter,
    setReload,
    canAddLearningManagers,
    activeOrganizationId,
    activeOrganizationType,
    setActiveOrganizationType,
    showFilter,
    userRole,
    setActiveOrganizationId,
    selectedRowKeys
}) => {

    // Modals
    const [showImportModal, setShowImportModal] = useState<boolean>(false);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [showUsersFilterModal, setShowUsersFilterModal] = useState(false);
    const [exportType, setExportType] = useState<'normal' | 'rights'>('normal');

    // Platform Access
    const [visiblePlatformAccessInfo, setVisiblePlatformAccessInfo] = useState<boolean>(false);
    const [validPlatformAccess, setValidPlatformAccess] = useState<boolean>(false);
    const [platformAccessInfo, setPlatformAccessInfo] = useState('');

    const t = useT();

    const canExportWithRights = session.special_rights?.includes('can_manage_user_rights');

    useEffect(() => {
        if (userRole !== 'SUPERVISION' && activeOrganizationType === 'CUSTOMER') {
            setPlatformAccessInfoMessage(activeOrganizationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrganizationId]);

    useEffect(() => {
        if (showFilter) {
            setShowUsersFilterModal(true);
        }
    }, [showFilter]);

    const setPlatformAccessInfoMessage = async (selectedCustomer: any) => {
        if (selectedCustomer) {
            let platformAccessData = await apiClient.request(`/api/v1/customers/${selectedCustomer}/platform-access-data`, {}, 'GET');

            let available = platformAccessData.platformAccess.max_students - platformAccessData.platformAccess.current_students_count;

            if (available < 0) available = 0;

            if (available === 0) {
                setValidPlatformAccess(false);
            } else {
                setValidPlatformAccess(true);
            }

            setPlatformAccessInfo(available + '/' + platformAccessData.platformAccess.max_students);
            setVisiblePlatformAccessInfo(true);
        } else {
            setVisiblePlatformAccessInfo(false);
        }
    };

    const menu = (
        <Menu>
            {activeOrganizationId && activeOrganizationType === 'ORGANIZATION' ?
                <Menu.Item>
                    <NavLink
                        to={!activeOrganizationId ? `/admins/add` : `/organizations/${activeOrganizationId}/admins/add`}>
                        <FormattedMessage id='general.admin' />
                    </NavLink>
                </Menu.Item>
                : null
            }

            {activeOrganizationId && activeOrganizationType === 'CUSTOMER' ?
                <Menu.Item>
                    <NavLink to={`/customer/${activeOrganizationId}/user/add/learning-managers`}>
                        <FormattedMessage id='general.learning_manager' />
                    </NavLink>
                </Menu.Item>
                : null
            }

            {activeOrganizationId && activeOrganizationType === 'CUSTOMER' ?
                <Menu.Item>
                    <NavLink to={`/customer/${activeOrganizationId}/user/add/students`}>
                        <FormattedMessage id='general.student' />
                    </NavLink>
                </Menu.Item>
                : null
            }
        </Menu>
    );

    return (
        <div>
            <FlexRow
                left={
                    <>
                        {userRole !== 'SUPERVISION' &&
                            <>
                                {userRole === 'SUPER_ADMIN' || userRole === 'DISTRIBUTOR_ADMIN' || userRole === 'RESELLER_ADMIN' || canAddLearningManagers
                                    ? (
                                        <Dropdown overlay={menu} placement="bottomLeft">
                                            <Button type="primary">
                                                <PlusOutlined />
                                                <span><FormattedMessage id="users.add_user" /></span>
                                                <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    ) : (
                                        <NavLink to={`/customer/${activeOrganizationId}/user/add/students`}>
                                            <Button type="primary" icon={<PlusOutlined />}>
                                                <FormattedMessage id="users.add_student" />
                                            </Button>
                                        </NavLink>
                                    )
                                }
                            </>
                        }

                        <Button type="primary" onClick={() => setShowUsersFilterModal(true)}>
                            <FormattedMessage id="general.filter" />
                        </Button>

                        {userRole !== 'SUPERVISION' &&
                            <>
                                <Button icon={<i className="fal fa-file-import" />} onClick={() => setShowImportModal(true)}>
                                    <span>
                                        <FormattedMessage id={t('users.import.import')} />
                                    </span>
                                </Button>

                                <Space.Compact block>
                                    {canExportWithRights ? (
                                        <Dropdown.Button
                                            trigger={['click']}
                                            placement="bottomRight"
                                            type="primary"
                                            menu={{
                                                items: [
                                                    {
                                                        label: (
                                                            <FormattedMessage id="users.export.export_with_rights" />
                                                        ),
                                                        key: 'rights',
                                                    },
                                                ],
                                                onClick: ({ key }) => {
                                                    setExportType(key as 'normal' | 'rights');
                                                    setShowExportModal(true);
                                                },
                                            }}
                                            onClick={() => {
                                                setExportType('normal');
                                                setShowExportModal(true);
                                            }}
                                        >
                                            <i
                                                className="fal fa-file-export"
                                                style={{ marginRight: 8 }}
                                            />
                                            <span>
                                                <FormattedMessage id="users.export.export" />
                                            </span>
                                        </Dropdown.Button>
                                    ) : (
                                        <Button
                                            icon={<i className="fal fa-file-export" />}
                                            onClick={() => {
                                                setExportType('normal');
                                                setShowExportModal(true);
                                            }}
                                        >
                                            <span>
                                                <FormattedMessage id="users.export.export" />
                                            </span>
                                        </Button>
                                    )}
                                </Space.Compact>

                                {visiblePlatformAccessInfo && activeOrganizationType === 'CUSTOMER' && (
                                    <label>
                                        <FormattedMessage
                                            id={t('users.available_student_access_licences')}
                                        />:{' '}
                                        <span style={{ color: validPlatformAccess ? '#52c419' : 'red' }}>
                                            {platformAccessInfo}
                                        </span>
                                    </label>
                                )}
                            </>
                        }
                    </>
                }
                right={
                    <>
                        <Search setReload={setReload} setSearchString={setSearch} />
                        <InstructionButton position='USER_MANAGEMENT' />
                    </>
                }
            />

            <div className={'table-filter-modal'}>
                <FilterModal
                    visible={showUsersFilterModal}
                    onCancel={setShowUsersFilterModal}
                    modules={{
                        filterBy: { visible: generalHelpers.isAdmin(session.active_user_type) },
                        customerSelect: { visible: generalHelpers.isAdmin(session.active_user_type), set: setActiveOrganizationId, setType: setActiveOrganizationType },
                        organizationSelect: { visible: generalHelpers.isAdmin(session.active_user_type), set: setActiveOrganizationId, setType: setActiveOrganizationType },
                        organizationalUnitId: { mode: 'multiple', visible: true },
                        departmentId: { mode: 'multiple', visible: true },
                        accountStatus: { visible: true },
                        absence: { visible: true },
                        userTypes: { visible: true },
                        registration: { visible: true },
                        newUsers: { visible: true },
                        anonymized: { visible: true },
                        customerFilter: { mode: 'multiple', visible: true },
                    }}
                    setFilter={setFilter}
                    load={setReload}
                    filter={filter}
                />
            </div>

            {userRole !== 'SUPERVISION' &&
                <>
                    <ImportModal
                        visible={showImportModal}
                        onCancel={() => setShowImportModal(false)}
                        afterSubmit={() => setShowImportModal(false)}
                        customerId={activeOrganizationType === "CUSTOMER" ? activeOrganizationId : null}
                        setActiveOrganizationId={setActiveOrganizationId}
                        userRole={userRole}
                    />

                    <UsersExportModal
                        visible={showExportModal}
                        onCancel={() => setShowExportModal(false)}
                        activeOrganizationId={activeOrganizationId}
                        activeOrganizationType={activeOrganizationType}
                        selectedRowKeys={selectedRowKeys}
                        exportType={exportType}
                        filter={filter}
                        search={search}
                    />
                </>
            }
        </div>
    );
};

export default connect(mapStateToProps)(UsersListToolbar);
